import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { publicApi as api, API } from "@/lib/api";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Check, MessageSquare, Clipboard, Download } from "lucide-react";

const STAGE_LABEL = {
    brand_dna: "Brand DNA", business_plan: "Business Plan", research: "Research",
    competitor_analysis: "Competitor Analysis",
    concepts: "Concepts", script: "Script", storyboard: "Storyboard",
    prompts: "Prompt Library", presentation: "Presentation", qc_review: "QC Review",
    post_production: "Post-Production",
};

const STAGE_ORDER = [
    "presentation", "brand_dna", "research", "competitor_analysis",
    "business_plan", "concepts", "script", "storyboard",
    "qc_review", "post_production",
];

export default function PublicReview() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [commentByStage, setCommentByStage] = useState({});

    useEffect(() => {
        api.get(`/public/review/${token}`)
            .then(({ data }) => { setData(data); setError(null); })
            .catch((e) => {
                const detail = e?.response?.data?.detail;
                const status = e?.response?.status;
                setError(status === 404
                    ? (detail || "This review link is invalid or has been disabled.")
                    : (detail || "Could not load review."));
                toast.error("Review link not found or disabled");
            });
    }, [token]);

    // Scroll to deep-linked stage (e.g. /share/{pid}/{token}#concepts)
    useEffect(() => {
        if (!data) return;
        const hash = (window.location.hash || "").replace("#", "");
        if (!hash) return;
        const target = document.getElementById(`public-stage-${hash}`);
        if (target) {
            // Defer one tick so the layout settles before scroll
            setTimeout(() => target.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
        }
    }, [data]);

    const act = async (stage, action) => {
        if (!name) { toast.error("Please enter your name first"); return; }
        try {
            const comment = commentByStage[stage] || "";
            await api.post(`/public/review/${token}/approval`, { name, email, stage, action, comment });
            toast.success(action === "approve" ? "Approved — thank you!" : "Changes requested — the team has been notified.");
            setCommentByStage(m => ({ ...m, [stage]: "" }));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed");
        }
    };

    if (error) return (
        <div className="min-h-screen bg-[#050505] text-zinc-100 grid place-items-center px-6"
             data-testid="public-review-not-found">
            <div className="max-w-md text-center space-y-4">
                <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-zinc-500">
                    Review link
                </div>
                <h1 className="font-display text-3xl">Link unavailable</h1>
                <p className="text-sm text-zinc-400 leading-relaxed">{error}</p>
                <p className="text-xs text-zinc-600">
                    If you received this from the agency, ask them to send a fresh link.
                </p>
            </div>
        </div>
    );
    if (!data) return <div className="min-h-screen bg-[#050505] text-zinc-500 grid place-items-center" data-testid="public-review-loading">Loading review…</div>;

    const agencyName = (data.agency?.agency_name || "").trim();
    const whiteLabel = !!data.agency?.white_label;
    const accent = data.agency?.primary_color || "#FF4400";

    const orderedStages = STAGE_ORDER
        .filter(k => data.stages && data.stages[k])
        .map(k => [k, data.stages[k]]);

    return (
        <div className="min-h-screen bg-[#050505] text-zinc-100" data-testid="public-review-page">
            <header className="border-b border-zinc-900 bg-[#080808] sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div
                            className="w-8 h-8 rounded-sm grid place-items-center font-display font-bold text-black"
                            style={{ backgroundColor: accent }}
                        >
                            {(agencyName || data.client?.company_name || "A").charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <div className="font-display font-bold" data-testid="public-header-title">
                                {data.client?.company_name || "Client"} · Review
                            </div>
                            <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
                                {data.project.title}
                            </div>
                        </div>
                    </div>
                    {!whiteLabel && (
                        <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500" data-testid="public-prepared-by">
                            {agencyName ? `Prepared by ${agencyName}` : "Powered by Furmais"}
                        </div>
                    )}
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-6 py-10 space-y-6">
                <div className="card-base p-5" data-testid="public-identity">
                    <div className="label-micro mb-3">Your details</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input data-testid="review-name" placeholder="Your name *" value={name}
                               onChange={e => setName(e.target.value)}
                               className="bg-black border border-zinc-800 rounded-sm px-3 py-2 outline-none focus:border-zinc-500" />
                        <input data-testid="review-email" placeholder="Email (optional)" value={email}
                               onChange={e => setEmail(e.target.value)}
                               className="bg-black border border-zinc-800 rounded-sm px-3 py-2 outline-none focus:border-zinc-500" />
                    </div>
                </div>

                {orderedStages.length === 0 && (
                    <div className="card-base p-8 text-center text-zinc-500" data-testid="public-empty">
                        No stages have been shared yet.
                    </div>
                )}

                {orderedStages.map(([stageKey, stage]) => (
                    <StageCard
                        key={stageKey}
                        stageKey={stageKey}
                        stage={stage}
                        token={token}
                        accent={accent}
                        comment={commentByStage[stageKey] || ""}
                        setComment={(v) => setCommentByStage(m => ({ ...m, [stageKey]: v }))}
                        onApprove={() => act(stageKey, "approve")}
                        onChanges={() => act(stageKey, "reject")}
                    />
                ))}
            </main>
            <Toaster theme="dark" />
        </div>
    );
}

function StageCard({ stageKey, stage, token, accent, comment, setComment, onApprove, onChanges }) {
    const label = STAGE_LABEL[stageKey] || stageKey;
    return (
        <div className="card-base p-6" id={`public-stage-${stageKey}`} data-testid={`public-stage-${stageKey}`}>
            <div className="flex items-center justify-between mb-5">
                <div>
                    <div className="label-micro" style={{ color: accent }}>{label}</div>
                    <div className="font-display text-2xl mt-1">{label}</div>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    {stage.status || "pending"}
                </span>
            </div>

            <StageBody stageKey={stageKey} stage={stage} token={token} />

            <div className="mt-5 flex flex-wrap gap-2 items-end">
                <textarea
                    data-testid={`public-comment-${stageKey}`}
                    rows={2} placeholder="Optional note to the team…"
                    value={comment} onChange={e => setComment(e.target.value)}
                    className="flex-1 bg-black border border-zinc-800 rounded-sm px-3 py-2 outline-none focus:border-zinc-500 min-w-[200px]"
                />
                <button
                    data-testid={`public-approve-${stageKey}`}
                    onClick={onApprove}
                    className="btn-primary text-xs px-3 py-1.5">
                    <Check className="w-3.5 h-3.5" /> Approve
                </button>
                <button
                    data-testid={`public-reject-${stageKey}`}
                    onClick={onChanges}
                    className="btn-secondary text-xs px-3 py-1.5">
                    <MessageSquare className="w-3.5 h-3.5" /> Request Changes
                </button>
            </div>
        </div>
    );
}

/* ============================ STAGE BODIES ============================ */

function StageBody({ stageKey, stage, token }) {
    const out = stage?.output || {};
    if (stageKey === "storyboard") return <PublicStoryboard stage={stage} token={token} />;
    if (stageKey === "presentation") return <PublicPresentation token={token} out={out} />;
    if (stageKey === "brand_dna") return <PublicBrandDna out={out} />;
    if (stageKey === "research") return <PublicResearch out={out} />;
    if (stageKey === "competitor_analysis") return <PublicCompetitor out={out} />;
    if (stageKey === "business_plan") return <PublicBusinessPlan out={out} />;
    if (stageKey === "concepts") return <PublicConcepts out={out} />;
    if (stageKey === "script") return <PublicScript out={out} />;
    if (stageKey === "qc_review") return <PublicQc out={out} />;
    if (stageKey === "post_production") return <PublicPostProduction out={out} />;
    // Fallback for stages we don't have a custom renderer for.
    return <GenericPreview out={out} />;
}

function PublicCompetitor({ out }) {
    const c = out?.competitor_analysis || {};
    const tiers = Array.isArray(c.competitors) ? c.competitors : [];
    const grouped = { 1: [], 2: [], 3: [] };
    tiers.forEach((x) => {
        const t = [1, 2, 3].includes(x.tier) ? x.tier : 2;
        grouped[t].push(x);
    });
    const tierLabel = { 1: "Hyperlocal", 2: "City-wide", 3: "National benchmark" };
    if (tiers.length === 0) return <GenericPreview out={c} />;
    return (
        <div className="space-y-4" data-testid="public-competitor">
            {[1, 2, 3].map((t) =>
                grouped[t].length === 0 ? null : (
                    <div key={t}>
                        <div className="label-micro mb-2">
                            Tier {t} · {tierLabel[t]} · {grouped[t].length}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {grouped[t].slice(0, 6).map((x, i) => (
                                <div
                                    key={`${t}-${i}`}
                                    className="border border-zinc-800 rounded-sm p-3 bg-[#0a0a0a]"
                                >
                                    <div className="text-sm font-display text-zinc-100">{x.name}</div>
                                    {x.location && (
                                        <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 mt-0.5">
                                            {x.location}
                                        </div>
                                    )}
                                    {x.positioning && (
                                        <div className="text-xs text-zinc-300 mt-2">{x.positioning}</div>
                                    )}
                                    {x.opportunity_for_client && (
                                        <div className="text-xs text-emerald-300 mt-2 border-l-2 border-emerald-500/30 pl-2">
                                            {x.opportunity_for_client}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )
            )}
            {c.strategic_positioning_recommendation && (
                <div className="border border-zinc-800 rounded-sm p-3 bg-[#0a0a0a]">
                    <div className="label-micro mb-1">Strategic positioning</div>
                    <div className="text-sm text-zinc-200">
                        {c.strategic_positioning_recommendation}
                    </div>
                </div>
            )}
        </div>
    );
}

function PublicQc({ out }) {
    const q = out?.qc_review || out || {};
    const status = q.overall_status || q.status;
    const findings = Array.isArray(q.findings)
        ? q.findings
        : (Array.isArray(q.issues) ? q.issues : []);
    const checklist = Array.isArray(q.checklist) ? q.checklist : [];
    if (!status && findings.length === 0 && checklist.length === 0) {
        return <GenericPreview out={q} />;
    }
    return (
        <div className="space-y-3" data-testid="public-qc">
            {status && (
                <div className="border border-zinc-800 rounded-sm p-3 bg-[#0a0a0a] flex items-center gap-2">
                    <span className="label-micro">QC Status</span>
                    <span className="text-sm text-zinc-100">{String(status)}</span>
                </div>
            )}
            {findings.length > 0 && (
                <MiniList label="Findings" value={findings.slice(0, 8)} />
            )}
            {checklist.length > 0 && (
                <MiniList label="Checklist" value={checklist.slice(0, 8)} />
            )}
            {q.summary && (
                <div className="border border-zinc-800 rounded-sm p-3 bg-[#0a0a0a]">
                    <div className="label-micro mb-1">Summary</div>
                    <div className="text-sm text-zinc-200 whitespace-pre-wrap">{q.summary}</div>
                </div>
            )}
        </div>
    );
}

function PublicPostProduction({ out }) {
    const p = out?.post_production || out || {};
    const edits = Array.isArray(p.edit_instructions)
        ? p.edit_instructions
        : (Array.isArray(p.instructions) ? p.instructions : []);
    const delivery = Array.isArray(p.delivery_specs)
        ? p.delivery_specs
        : (Array.isArray(p.deliverables) ? p.deliverables : []);
    if (edits.length === 0 && delivery.length === 0 && !p.summary) {
        return <GenericPreview out={p} />;
    }
    return (
        <div className="space-y-3" data-testid="public-post-production">
            {p.summary && (
                <div className="border border-zinc-800 rounded-sm p-3 bg-[#0a0a0a]">
                    <div className="label-micro mb-1">Summary</div>
                    <div className="text-sm text-zinc-200 whitespace-pre-wrap">{p.summary}</div>
                </div>
            )}
            {edits.length > 0 && (
                <MiniList label="Edit instructions" value={edits.slice(0, 8)} />
            )}
            {delivery.length > 0 && (
                <MiniList label="Delivery specs" value={delivery.slice(0, 8)} />
            )}
        </div>
    );
}

function GenericPreview({ out }) {
    const text = JSON.stringify(out, null, 2);
    const truncated = text.length > 1200 ? text.slice(0, 1200) + "\n…" : text;
    return (
        <pre className="text-xs font-mono bg-black border border-zinc-900 rounded-sm p-4 overflow-x-auto whitespace-pre-wrap text-zinc-300">
            {truncated}
        </pre>
    );
}

function PublicPresentation({ token, out }) {
    // Embedded browser PDF viewer — works cross-browser, no auth.
    const pdfUrl = `${API}/public/review/${token}/presentation.pdf`;
    const pptxUrl = `${API}/public/review/${token}/presentation.pptx`;
    const title = out?.presentation?.title || "Pitch Deck";
    const summary = out?.presentation?.executive_summary || "";
    return (
        <div className="space-y-4" data-testid="public-presentation">
            {summary && (
                <div className="text-sm text-zinc-300 border-l-2 border-zinc-700 pl-4">
                    {summary}
                </div>
            )}
            <div className="aspect-[16/10] w-full border border-zinc-800 rounded-sm overflow-hidden bg-black">
                <iframe
                    data-testid="public-presentation-iframe"
                    src={`${pdfUrl}#toolbar=0&navpanes=0`}
                    title={title}
                    className="w-full h-full"
                    loading="lazy"
                />
            </div>
            <div className="flex gap-2">
                <a href={pdfUrl} target="_blank" rel="noreferrer"
                   data-testid="public-pdf-download"
                   className="btn-secondary text-xs px-3 py-1.5">
                    <Download className="w-3.5 h-3.5" /> Open PDF
                </a>
                <a href={pptxUrl}
                   data-testid="public-pptx-download"
                   className="btn-secondary text-xs px-3 py-1.5">
                    <Download className="w-3.5 h-3.5" /> Download PPTX
                </a>
            </div>
        </div>
    );
}

function PublicBrandDna({ out }) {
    const b = out?.brand_dna || {};
    const essence = b.essence || b.brand_essence;
    return (
        <div className="space-y-4" data-testid="public-brand-dna">
            {essence && (
                <div className="border border-zinc-800 rounded-sm p-4 bg-[#0a0a0a]">
                    <div className="label-micro mb-1">Essence</div>
                    <div className="font-display text-lg">{essence}</div>
                </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <MiniKV label="Positioning" value={b.positioning_statement || b.positioning} />
                <MiniKV label="Core Promise" value={b.core_promise || b.brand_promise || b.promise} />
                <MiniKV label="Voice & Tone" value={b.tone_of_voice || b.voice_tone} />
                <MiniKV label="Audience" value={b.target_audience || b.audience} />
                <MiniKV label="Emotional Hook" value={b.emotional_hook} />
                <MiniList label="Differentiation" value={b.differentiation_vs_competitors} />
            </div>
            {Array.isArray(b.brand_personality) && b.brand_personality.length > 0 && (
                <MiniList label="Personality" value={b.brand_personality} />
            )}
        </div>
    );
}

function PublicResearch({ out }) {
    const r = out?.research || {};
    const items = [
        ["Local Market Insights", r.local_market_insights || r.audience_insights || r.audience],
        ["Audience Segments", r.audience_segments],
        ["Pain Points", r.pain_points],
        ["Buying Triggers", r.buying_triggers],
        ["Competitor Gaps", r.competitor_gap_opportunities || r.competitor_gaps || r.gaps],
        ["Growth Angles", r.growth_angles || r.opportunity],
    ].filter(([, v]) => v && (Array.isArray(v) ? v.length > 0 : true));
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="public-research">
            {items.map(([label, v]) => <MiniList key={label} label={label} value={v} />)}
            {items.length === 0 && <GenericPreview out={r} />}
        </div>
    );
}

function PublicBusinessPlan({ out }) {
    const b = out?.business_plan || {};
    const roadmap = Array.isArray(b.execution_roadmap) ? b.execution_roadmap : [];
    return (
        <div className="space-y-4" data-testid="public-business-plan">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <MiniKV label="Positioning" value={b.positioning} />
                <MiniKV label="Objective" value={b.business_objective || b.objective || b.primary_goal} />
                <MiniKV label="Revenue Model" value={b.revenue_model} />
                <MiniKV label="Pricing" value={b.pricing_model} />
                <MiniList label="Channels" value={b.channels} />
                <MiniList label="KPIs" value={b.kpis} />
            </div>
            {roadmap.length > 0 && (
                <div>
                    <div className="label-micro mb-2">Roadmap</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {roadmap.slice(0, 3).map((ph, i) => (
                            <div key={i} className="border border-zinc-800 rounded-sm p-3 bg-[#0a0a0a]">
                                <div className="label-micro">{ph.phase || `Phase ${i + 1}`} {ph.weeks ? `· ${ph.weeks}` : ""}</div>
                                <ul className="text-sm text-zinc-200 mt-2 space-y-1">
                                    {(ph.actions || []).slice(0, 3).map((a, j) => <li key={j}>• {a}</li>)}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function PublicConcepts({ out }) {
    const items = Array.isArray(out?.concepts) ? out.concepts : [];
    if (items.length === 0) return <GenericPreview out={out} />;
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="public-concepts">
            {items.slice(0, 3).map((c, i) => (
                <div key={c.id || i} className="border border-zinc-800 rounded-sm p-4 bg-[#0a0a0a]">
                    <div className="label-micro mb-1">Concept {i + 1}</div>
                    <div className="font-display text-lg">{c.name || c.title}</div>
                    <div className="text-sm text-zinc-400 mt-2">{c.big_idea || c.tagline || c.one_liner}</div>
                    <div className="text-sm text-zinc-200 mt-3">{c.hook || c.description || c.summary}</div>
                    {c.emotional_angle && (
                        <div className="text-xs text-zinc-500 mt-3 border-t border-zinc-800 pt-3">
                            <span className="text-zinc-600">Why it wins: </span>{c.why_it_beats_competitors || c.emotional_angle}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

function PublicScript({ out }) {
    const scripts = Array.isArray(out?.scripts) ? out.scripts : (out?.script ? [out.script] : []);
    if (scripts.length === 0) return <GenericPreview out={out} />;
    return (
        <div className="space-y-4" data-testid="public-scripts">
            {scripts.map((s, i) => (
                <div key={i} className="border border-zinc-800 rounded-sm p-4 bg-[#0a0a0a]">
                    <div className="flex items-center justify-between mb-3">
                        <div className="label-micro">{s.label || s.variant || `Variant ${i + 1}`}</div>
                        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                            {s.duration_seconds ? `${s.duration_seconds}s` : ""} {s.language || ""}
                        </span>
                    </div>
                    <MiniKV label="Hook" value={s.hook} />
                    <MiniKV label="Voiceover" value={s.voiceover} />
                    <MiniKV label="CTA" value={s.cta} />
                </div>
            ))}
        </div>
    );
}

function MiniKV({ label, value }) {
    if (!value) return null;
    return (
        <div className="border border-zinc-800 rounded-sm p-3 bg-[#0a0a0a]">
            <div className="label-micro mb-1">{label}</div>
            <div className="text-sm text-zinc-200 whitespace-pre-wrap">{typeof value === "string" ? value : JSON.stringify(value)}</div>
        </div>
    );
}

function MiniList({ label, value }) {
    if (!value) return null;
    const items = Array.isArray(value) ? value : [value];
    return (
        <div className="border border-zinc-800 rounded-sm p-3 bg-[#0a0a0a]">
            <div className="label-micro mb-1">{label}</div>
            <ul className="text-sm text-zinc-200 space-y-2">
                {items.slice(0, 6).map((v, i) => {
                    if (typeof v === "string") return <li key={i}>• {v}</li>;
                    if (v && typeof v === "object") {
                        // Common agent shapes: {name, who, motivators, barriers},
                        // {gap, how_to_exploit}, {title, description}, etc.
                        const primary = v.title || v.name || v.gap || v.insight || v.text;
                        const secondary = v.how_to_exploit || v.description || v.details || v.who;
                        if (primary || secondary) {
                            return (
                                <li key={i}>
                                    {primary && <div>• <span className="text-zinc-100">{primary}</span></div>}
                                    {secondary && <div className="ml-4 text-xs text-zinc-400 mt-0.5">{secondary}</div>}
                                </li>
                            );
                        }
                    }
                    return <li key={i}>• {String(v)}</li>;
                })}
            </ul>
        </div>
    );
}

/* ============================ STORYBOARD ============================ */

function PublicStoryboard({ stage, token }) {
    const sb = stage?.output?.storyboard;
    if (!sb) return <div className="text-sm text-zinc-500">Storyboard not available yet.</div>;
    const copy = async (text, label) => {
        if (!text) return;
        try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
        catch { toast.error("Copy failed"); }
    };
    return (
        <div className="space-y-5" data-testid="public-storyboard">
            {sb.total_duration_seconds && (
                <div className="text-xs font-mono text-zinc-500">Total duration: {sb.total_duration_seconds}s</div>
            )}
            {(sb.scenes || []).map(scene => (
                <div key={scene.scene_number} className="border border-zinc-900 rounded-sm p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="label-micro">Scene {scene.scene_number}</div>
                        <div className="text-zinc-300 text-sm">{scene.title}</div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {(scene.shots || []).map(shot => {
                            const hasImage = !!shot.visual_storage_path;
                            const imagePrompt = shot.image_prompt || shot.visual_description || "";
                            return (
                                <div key={shot.shot_id} className="border border-zinc-800 rounded-sm bg-[#0a0a0a] p-4 space-y-3"
                                     data-testid={`public-shot-${shot.shot_id}`}>
                                    {hasImage ? (
                                        <div className="aspect-video bg-black rounded-sm overflow-hidden border border-zinc-900">
                                            <img
                                                src={`${API}/public/review/${token}/file/${shot.visual_storage_path}`}
                                                alt={`Shot ${shot.shot_number}`}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                        </div>
                                    ) : (
                                        <div className="relative aspect-video rounded-sm overflow-hidden border border-[#FF4400]/40"
                                             style={{
                                                 background:
                                                     "radial-gradient(ellipse at 30% 20%, rgba(255,68,0,0.12), transparent 60%), " +
                                                     "linear-gradient(135deg, #0c0c0c 0%, #181818 100%)",
                                             }}>
                                            <div className="absolute inset-3 border border-dashed border-zinc-700/60 rounded-sm pointer-events-none" />
                                            <div className="absolute inset-0 p-4 flex flex-col justify-between">
                                                <div>
                                                    <div className="text-[9px] uppercase tracking-wider font-mono text-[#FF4400]">
                                                        Shot {shot.shot_number} · {shot.shot_id}
                                                    </div>
                                                    <div className="text-sm font-display text-zinc-100 mt-1 line-clamp-2">
                                                        {shot.subject || shot.action || "Untitled shot"}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[10px] font-mono text-zinc-400">
                                                    <div><span className="text-zinc-600">Cam:</span> <span className="text-zinc-200">{shot.camera_movement || "—"}</span></div>
                                                    <div><span className="text-zinc-600">Light:</span> <span className="text-zinc-200">{shot.lighting || "—"}</span></div>
                                                    <div><span className="text-zinc-600">Mood:</span> <span className="text-zinc-200">{shot.emotion || "—"}</span></div>
                                                    <div><span className="text-zinc-600">Trans:</span> <span className="text-zinc-200">{shot.transition || "—"}</span></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                                        <span>Shot {shot.shot_number} · {shot.shot_id}</span>
                                        <span>{shot.duration_seconds || "—"}s</span>
                                    </div>
                                    <div className="text-sm text-zinc-100">{shot.visual_description}</div>
                                    {imagePrompt && (
                                        <div>
                                            <div className="flex items-center justify-between">
                                                <div className="label-micro">Image prompt</div>
                                                <button onClick={() => copy(imagePrompt, "Image prompt")}
                                                        data-testid={`public-copy-img-${shot.shot_id}`}
                                                        className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 hover:text-[#FF4400] flex items-center gap-1">
                                                    <Clipboard className="w-3 h-3" /> Copy
                                                </button>
                                            </div>
                                            <div className="text-xs font-mono text-zinc-300 bg-black border border-zinc-900 p-2.5 rounded-sm mt-1 whitespace-pre-wrap break-words max-h-32 overflow-y-auto">
                                                {imagePrompt}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
}
